import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Landmark, Loader2, CheckCircle2, AlertCircle, ExternalLink, Wallet, Clock, FileText, History, XCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { money } from "@/lib/format";

function CenterPayoutHistory() {
  const { data, isLoading } = useQuery({
    queryKey: ["mgr-payout-records"],
    queryFn: () => api.get("/manager/me/payout-records").then((r) => r.data.records),
  });
  if (isLoading) return <div className="py-4 text-center"><Loader2 className="animate-spin inline text-[#144F75]" size={16} /></div>;
  const recs = data || [];
  if (!recs.length) return <p className="text-xs text-[#8FA89B] py-2">No payout history yet.</p>;
  return (
    <div className="space-y-2 mt-2" data-testid="center-payout-history">
      {recs.map((r) => (
        <div key={r.id} className="flex items-center justify-between text-sm bg-[#F9F7F3] rounded-lg px-3 py-2.5">
          <div>
            <span className={`text-[10px] rounded-full px-2 py-0.5 mr-2 ${r.status === "paid" ? "bg-[#5C8A72]/15 text-[#5C8A72]" : "bg-red-100 text-red-600"}`}>
              {r.status === "paid" ? <CheckCircle2 className="inline" size={9} /> : <XCircle className="inline" size={9} />} {r.status}
            </span>
            <span className="text-[#8FA89B] text-xs">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}</span>
          </div>
          <div className="text-right">
            <p className={`font-semibold ${r.status === "paid" ? "text-[#5C8A72]" : "text-red-500"}`}>{money(Number(r.amount || 0))}</p>
            {r.failure_reason && <p className="text-[10px] text-red-500">{r.failure_reason.slice(0, 40)}</p>}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function CenterPayoutPanel() {
  const status = useQuery({ queryKey: ["mgr-connect"], queryFn: () => api.get("/manager/connect/status").then((r) => r.data), retry: false });
  const ledger = useQuery({ queryKey: ["mgr-ledger"], queryFn: () => api.get("/manager/me/ledger").then((r) => r.data) });
  const stripeBal = useQuery({ queryKey: ["mgr-connect-balance"], queryFn: () => api.get("/manager/connect/balance").then((r) => r.data), retry: false, enabled: !!status.data?.connected });
  const [busy, setBusy] = useState(false);
  const [stmtMonth, setStmtMonth] = useState(new Date().toISOString().slice(0, 7));
  const [stmtBusy, setStmtBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const s = status.data || {};
  const l = ledger.data || {};
  const ready = s.connected && s.payouts_enabled;
  const pending = s.connected && !s.payouts_enabled;

  const onboard = async () => {
    setBusy(true);
    try {
      const returnUrl = `${window.location.origin}/manager?connect=done`;
      const { data } = await api.post("/manager/connect/onboard", { return_url: returnUrl, refresh_url: returnUrl });
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };

  const downloadStatement = async () => {
    if (!stmtMonth) { toast.error("Pick a month first."); return; }
    setStmtBusy(true);
    try {
      const res = await api.get(`/manager/me/statement?month=${stmtMonth}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `eyw-center-statement-${stmtMonth}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Statement downloaded.");
    } catch { toast.error("Could not generate the statement."); }
    finally { setStmtBusy(false); }
  };

  return (
    <div data-testid="center-payout-panel">
      <div className="mb-5">
        <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><Landmark size={22} className="text-[#144F75]" /> Center payouts</h2>
        <p className="text-sm text-[#4A5D6B] mt-1">Your Center's location-use share (10% on flagship in-person appointments). Connect a payout account to receive transfers.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="eyw-card p-5" data-testid="center-available"><p className="text-[11px] uppercase tracking-wide text-[#5C8A72] flex items-center gap-1"><Wallet size={11} /> Available</p><p className="font-serif text-2xl text-[#5C8A72]">{money((l.available ?? 0))}</p></div>
        <div className="eyw-card p-5" data-testid="center-held"><p className="text-[11px] uppercase tracking-wide text-[#B5794E] flex items-center gap-1"><Clock size={11} /> Held</p><p className="font-serif text-2xl text-[#B5794E]">${(l.held ?? 0).toFixed(2)}</p></div>
        <div className="eyw-card p-5" data-testid="center-paid"><p className="text-[11px] uppercase tracking-wide text-[#144F75]">Paid out</p><p className="font-serif text-2xl text-[#144F75]">{money((l.paid ?? 0))}</p></div>
        <div className="eyw-card p-5" data-testid="center-earned"><p className="text-[11px] uppercase tracking-wide text-[#8FA89B]">Earned to date</p><p className="font-serif text-2xl text-[#4A5D6B]">{money((l.earned_to_date ?? 0))}</p></div>
      </div>

      <div className="eyw-card p-5 border-l-4 border-l-[#635BFF]" data-testid="center-connect-card">
        {stripeBal.data?.available != null && (
          <p className="text-xs text-[#4A5D6B] mb-2" data-testid="center-stripe-balance">
            In the center's Stripe account: <span className="font-medium text-[#5C8A72]">{money(stripeBal.data.available)} available</span>
            {stripeBal.data.pending > 0 && <> · {money(stripeBal.data.pending)} on the way</>} — auto-paid to the bank daily.
          </p>
        )}
        <h3 className="font-serif text-lg text-[#1A242B] mb-3">Payout account</h3>
        {status.isLoading ? (
          <Loader2 className="animate-spin text-[#144F75]" size={18} />
        ) : ready ? (
          <p className="text-sm text-[#5C8A72] flex items-center gap-1.5" data-testid="center-connect-ready"><CheckCircle2 size={15} /> Connected — this Center is set to receive payouts.</p>
        ) : pending ? (
          <>
            <p className="text-sm text-[#B5794E] flex items-center gap-1.5 mb-3" data-testid="center-connect-pending"><AlertCircle size={15} /> Almost there — Stripe still needs a few details.</p>
            <button onClick={onboard} disabled={busy} data-testid="center-connect-continue" className="eyw-btn !py-2 text-sm">{busy ? <Loader2 className="animate-spin" size={14} /> : <ExternalLink size={14} />} Finish setup</button>
          </>
        ) : (
          <>
            <p className="text-sm text-[#4A5D6B] mb-3" data-testid="center-connect-none">Connect a Stripe payout account so your Center receives its location-use share directly. Setup is handled securely by Stripe.</p>
            <button onClick={onboard} disabled={busy} data-testid="center-connect-setup" className="eyw-btn !py-2 text-sm">{busy ? <Loader2 className="animate-spin" size={14} /> : <Landmark size={14} />} Set up payouts</button>
          </>
        )}
        {s.error && <p className="text-xs text-[#B45151] mt-2">{String(s.error).slice(0, 140)}</p>}
      </div>

      <div className="eyw-card p-5 mt-4" data-testid="center-statement-card">
        <h3 className="font-serif text-lg text-[#1A242B] mb-1 flex items-center gap-2"><FileText size={16} className="text-[#144F75]" /> Monthly earnings statement</h3>
        <p className="text-sm text-[#4A5D6B] mb-3">Download a PDF statement of your Center's earnings for any month — handy for taxes and bookkeeping.</p>
        <div className="flex flex-wrap items-center gap-2">
          <input type="month" value={stmtMonth} onChange={(e) => setStmtMonth(e.target.value)} data-testid="center-statement-month" className="eyw-input !py-1.5 text-sm" />
          <button onClick={downloadStatement} disabled={stmtBusy} data-testid="center-statement-download" className="eyw-btn !py-2 text-sm">
            {stmtBusy ? <Loader2 className="animate-spin" size={14} /> : <FileText size={14} />} Download PDF
          </button>
        </div>
      </div>

      <div className="eyw-card p-5 mt-4" data-testid="center-history-card">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-serif text-lg text-[#1A242B] flex items-center gap-2"><History size={16} className="text-[#144F75]" /> Payout history</h3>
          <button onClick={() => setShowHistory(!showHistory)} data-testid="toggle-center-history" className="text-xs text-[#4A5D6B] hover:text-[#144F75]">{showHistory ? "Hide" : "Show"}</button>
        </div>
        {showHistory && <CenterPayoutHistory />}
      </div>
    </div>
  );
}
